import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import LocationFourAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/locations/location-four/location-four.entity';
import LocationOneAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/locations/location-one/location-one.entity';
import LocationTwoAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/locations/location-two/location-two.entity';
import LocationThreeAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/locations/location-three/location-three.entity';
import {
  andAllWhere,
  andWhere,
  buildQuery,
  noop,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  select,
  where,
} from '@albatrosdeveloper/ave-utils-npm/lib/utils/query.util';
import { HttpClient } from '@angular/common/http';
import {
  IPagination,
  IPaginationResult,
  IRequestResult,
} from '../../../../common/interfaces';
import { ICreateLocationFour } from './interfaces/create-location-four.interface';
import { IUpdateLocationFour } from './interfaces/update-location-four.interface';
import { environment as env } from '../../../../../environments/environment.prod';
import { StatusEnum } from '@albatrosdeveloper/ave-models-package/lib/enums';
import { AbstractService } from '../../../../services/abstract.service';

const apiUrl = env.API_LOCATION_URL;
const moduleName = 'location_four';

@Injectable({
  providedIn: 'root',
})
export class LocationFourService extends AbstractService<
  LocationFourAttributes,
  ICreateLocationFour,
  IUpdateLocationFour
> {
  constructor(http: HttpClient) {
    super(apiUrl, moduleName, http);
  }

  /**
 * Pagination
 * @param options Pagination options
 * @param locationOneCodeList location-one codes filter
 * @param locationTwoCodeList location-two codes filter
 * @param locationThreeCodeList location-three codes filter
 * @returns Paginated data
 */
  findAllWithCount(
    options: IPagination<LocationFourAttributes>,
    locationOneCodeList: string[],
    locationTwoCodeList: string[],
    locationThreeCodeList: string[],
    statusIdList: number[]
  ): Observable<IPaginationResult<LocationFourAttributes>> {

    const query = buildQuery<LocationFourAttributes>(
      andWhere('_deleted', Ops.not(true)),
      options.search ? andWhere("code", Ops.like(options.search ?? "", "i")) : noop(),
      options.search ? orWhere("name", Ops.like(options.search ?? "", "i")) : noop(),
      locationOneCodeList && locationOneCodeList.length > 0 ? where('locationOne.code' as keyof LocationFourAttributes, Ops.in(...locationOneCodeList)) : noop(),
      locationTwoCodeList && locationTwoCodeList.length > 0 ? where('locationTwo.code' as keyof LocationFourAttributes, Ops.in(...locationTwoCodeList)) : noop(),
      locationThreeCodeList && locationThreeCodeList.length > 0 ? where('locationThree.code' as keyof LocationFourAttributes, Ops.in(...locationThreeCodeList)) : noop(),
      statusIdList && statusIdList.length > 0 ? where('statusId' as keyof LocationFourAttributes, Ops.in(...statusIdList)) : noop(),
      orderBy(
        options.sort ?? "code",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<LocationFourAttributes>>(API_URL);
  }

  /**
   * Find to combo box
   * @returns Data
   */
  findToComboBox(locationThree?): Observable<IRequestResult<LocationFourAttributes[]>> {
    const query = buildQuery<LocationFourAttributes>(
      where('statusId', StatusEnum.ACTIVE),
      locationThree ? andWhere('locationThree.code' as keyof LocationFourAttributes, Ops.eq(locationThree)) : noop(),
      select(['_id','code', 'name']),
      orderBy('code', OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<LocationFourAttributes[]>>(API_URL);
  }
}
